// =============================================================================
// Admin Investor Detail Page
// =============================================================================

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  PencilIcon,
  PlusIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  XMarkIcon,
  CheckIcon,
  TrashIcon,
  EyeIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, DocumentViewerModal } from '../../components/ui';
import { adminService, InvestorDetails, DealWithStats, InvestorDistribution, InvestorDocument, InvestmentContribution, ShareClass } from '../../services/admin.service';
import { getApiError, formatPhoneInput, formatPhoneDisplay, stripPhoneFormat } from '../../services/api';
import type { InvestorProfileType } from '../../types';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Types
// =============================================================================

interface ProfileFormData {
  type: InvestorProfileType;
  legalName?: string;
  entityName?: string;
  entityType?: string;
  stateOfFormation?: string;
  authorizedSigner?: string;
  ssn?: string;
  fein?: string;
  bankName?: string;
  bankAccountType?: 'CHECKING' | 'SAVINGS';
  routingNumber?: string;
  accountNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

// =============================================================================
// Edit Investor Modal
// =============================================================================

interface EditInvestorModalProps {
  isOpen: boolean;
  investor: InvestorDetails | null;
  onClose: () => void;
  onSaved: () => void;
}

function EditInvestorModal({ isOpen, investor, onClose, onSaved }: EditInvestorModalProps) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canDeleteInvestor = hasPermission('investors', 'delete');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && investor) {
      // Use firstName/lastName if available, otherwise parse from name field
      let first = investor.firstName || '';
      let last = investor.lastName || '';

      if (!first && !last && investor.name) {
        // Parse name field as fallback (split on first space)
        const nameParts = investor.name.trim().split(' ');
        first = nameParts[0] || '';
        last = nameParts.slice(1).join(' ') || '';
      }

      setFirstName(first);
      setLastName(last);
      setEmail(investor.email || '');
      setPhone(formatPhoneDisplay(investor.phone) || '');
      setError(null);
      setShowDeleteConfirm(false);
    }
  }, [isOpen, investor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!investor) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const phoneDigits = stripPhoneFormat(phone);
      await adminService.updateInvestor(investor.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() !== investor.email ? email.trim() : undefined,
        phone: phoneDigits || undefined,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!investor || deleteConfirmText.toLowerCase() !== 'proceed') return;

    setError(null);
    setIsDeleting(true);

    try {
      await adminService.deleteInvestor(investor.id);
      navigate('/admin/investors');
    } catch (err) {
      setError(getApiError(err).message);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen || !investor) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Investor</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {showDeleteConfirm ? (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">Are you sure you want to delete this investor?</p>
              <p className="text-sm text-red-600 mt-1">This action cannot be undone. All investor data, profiles, and investment records will be permanently removed.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm deletion:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <button
                type="button"
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDelete}
                disabled={isDeleting || deleteConfirmText.toLowerCase() !== 'proceed'}
              >
                {isDeleting ? 'Deleting...' : 'Delete Investor'}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="editEmail" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="editEmail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="editFirstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  id="editFirstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="editLastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="editLastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="editPhone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                id="editPhone"
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="555-123-4567"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>

            {canDeleteInvestor && (
              <div className="pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Delete This Investor
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Add Profile Modal
// =============================================================================

interface AddProfileModalProps {
  isOpen: boolean;
  investorId: string;
  onClose: () => void;
  onCreated: () => void;
}

function AddProfileModal({ isOpen, investorId, onClose, onCreated }: AddProfileModalProps) {
  const { hasPermission } = usePermissions();
  const canViewSensitive = hasPermission('investors', 'sensitive');
  const [formData, setFormData] = useState<ProfileFormData>({
    type: 'INDIVIDUAL',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // For TRUST and IRA: toggle between SSN or FEIN
  const [taxIdType, setTaxIdType] = useState<'ssn' | 'fein'>('ssn');
  // For TRUST and IRA: toggle between Legal Name or Entity Name
  const [nameType, setNameType] = useState<'legal' | 'entity'>('legal');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await adminService.createInvestorProfile(investorId, formData);
      await onCreated();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // TRUST and IRA are special - they can use either SSN/FEIN and either Legal Name/Entity Name
  const isTrustOrIra = formData.type === 'TRUST' || formData.type === 'IRA';
  // Standard entities (LLC, CORPORATION, PARTNERSHIP) always use Entity Name and FEIN
  const isStandardEntity = formData.type === 'LLC' || formData.type === 'CORPORATION' || formData.type === 'PARTNERSHIP';
  // Individual always uses Legal Name and SSN
  const isIndividual = formData.type === 'INDIVIDUAL';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Add Investment Profile</h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
          {/* Profile Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Type *</label>
            <div className="grid grid-cols-3 gap-2">
              {(['INDIVIDUAL', 'LLC', 'TRUST', 'CORPORATION', 'PARTNERSHIP', 'IRA'] as InvestorProfileType[]).map(
                (type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFormData({ ...formData, type })}
                    className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      formData.type === type
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {type}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Personal/Entity Info */}
          <div className="grid grid-cols-2 gap-4">
            {/* INDIVIDUAL: Always Legal Name */}
            {isIndividual && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name *</label>
                <input
                  type="text"
                  value={formData.legalName || ''}
                  onChange={(e) => setFormData({ ...formData, legalName: e.target.value, entityName: undefined })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Full legal name"
                />
              </div>
            )}

            {/* TRUST/IRA: Toggle between Legal Name or Entity Name */}
            {isTrustOrIra && (
              <>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name Type *</label>
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        setNameType('legal');
                        setFormData({ ...formData, entityName: undefined });
                      }}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        nameType === 'legal'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Legal Name (Individual)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setNameType('entity');
                        setFormData({ ...formData, legalName: undefined });
                      }}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        nameType === 'entity'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Entity Name
                    </button>
                  </div>
                  {nameType === 'legal' ? (
                    <input
                      type="text"
                      value={formData.legalName || ''}
                      onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Full legal name"
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData.entityName || ''}
                      onChange={(e) => setFormData({ ...formData, entityName: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Trust or IRA entity name"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State of Formation</label>
                  <input
                    type="text"
                    value={formData.stateOfFormation || ''}
                    onChange={(e) => setFormData({ ...formData, stateOfFormation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Delaware"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Authorized Signer / Trustee</label>
                  <input
                    type="text"
                    value={formData.authorizedSigner || ''}
                    onChange={(e) => setFormData({ ...formData, authorizedSigner: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Name of trustee or authorized signer"
                  />
                </div>
              </>
            )}

            {/* Standard Entities (LLC, CORPORATION, PARTNERSHIP): Always Entity Name */}
            {isStandardEntity && (
              <>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entity Name *</label>
                  <input
                    type="text"
                    value={formData.entityName || ''}
                    onChange={(e) => setFormData({ ...formData, entityName: e.target.value, legalName: undefined })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Legal entity name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State of Formation</label>
                  <input
                    type="text"
                    value={formData.stateOfFormation || ''}
                    onChange={(e) => setFormData({ ...formData, stateOfFormation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Delaware"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Authorized Signer</label>
                  <input
                    type="text"
                    value={formData.authorizedSigner || ''}
                    onChange={(e) => setFormData({ ...formData, authorizedSigner: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Name of authorized signer"
                  />
                </div>
              </>
            )}
          </div>

          {/* Tax ID */}
          {canViewSensitive ? (
          <div>
            {/* INDIVIDUAL: Always SSN */}
            {isIndividual && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-1">SSN</label>
                <input
                  type="text"
                  value={formData.ssn || ''}
                  onChange={(e) => setFormData({ ...formData, ssn: e.target.value, fein: undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="XXX-XX-XXXX"
                />
              </>
            )}

            {/* TRUST/IRA: Toggle between SSN or FEIN */}
            {isTrustOrIra && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID Type</label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setTaxIdType('ssn');
                      setFormData({ ...formData, fein: undefined });
                    }}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      taxIdType === 'ssn'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    SSN
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTaxIdType('fein');
                      setFormData({ ...formData, ssn: undefined });
                    }}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      taxIdType === 'fein'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    FEIN
                  </button>
                </div>
                <input
                  type="text"
                  value={taxIdType === 'ssn' ? formData.ssn || '' : formData.fein || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      [taxIdType === 'ssn' ? 'ssn' : 'fein']: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder={taxIdType === 'ssn' ? 'XXX-XX-XXXX' : 'XX-XXXXXXX'}
                />
              </>
            )}

            {/* Standard Entities: Always FEIN */}
            {isStandardEntity && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-1">FEIN (Tax ID)</label>
                <input
                  type="text"
                  value={formData.fein || ''}
                  onChange={(e) => setFormData({ ...formData, fein: e.target.value, ssn: undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="XX-XXXXXXX"
                />
              </>
            )}

            <p className="mt-1 text-xs text-gray-500">This will be encrypted and stored securely.</p>
          </div>
          ) : (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">Tax ID</h3>
            <p className="text-sm text-gray-500 italic">You do not have permission to edit sensitive data.</p>
          </div>
          )}

          {/* Banking Info */}
          {canViewSensitive ? (
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Banking Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                <input
                  type="text"
                  value={formData.bankName || ''}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Chase Bank"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                <select
                  value={formData.bankAccountType || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, bankAccountType: e.target.value as 'CHECKING' | 'SAVINGS' })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select type</option>
                  <option value="CHECKING">Checking</option>
                  <option value="SAVINGS">Savings</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number</label>
                <input
                  type="text"
                  value={formData.routingNumber || ''}
                  onChange={(e) => setFormData({ ...formData, routingNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="9 digits"
                  maxLength={9}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                <input
                  type="text"
                  value={formData.accountNumber || ''}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Account number"
                />
              </div>
            </div>
          </div>
          ) : (
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Banking Information</h3>
            <p className="text-sm text-gray-500 italic">You do not have permission to edit sensitive data.</p>
          </div>
          )}

          {/* Address */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Mailing Address</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <input
                  type="text"
                  value={formData.address || ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={formData.state || ''}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                  <input
                    type="text"
                    value={formData.zipCode || ''}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Profile'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Edit Profile Modal
// =============================================================================

interface EditProfileModalProps {
  profile: InvestorDetails['profiles'][0] | null;
  onClose: () => void;
  onSaved: () => void;
}

function EditProfileModal({ profile, onClose, onSaved }: EditProfileModalProps) {
  const { hasPermission } = usePermissions();
  const canDeleteInvestor = hasPermission('investors', 'delete');
  const canViewSensitive = hasPermission('investors', 'sensitive');
  const [formData, setFormData] = useState<ProfileFormData>({
    type: 'INDIVIDUAL',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taxIdType, setTaxIdType] = useState<'ssn' | 'fein'>('ssn');

  useEffect(() => {
    if (profile) {
      setFormData({
        type: profile.type as InvestorProfileType,
        legalName: profile.legalName || '',
        entityName: profile.entityName || '',
        entityType: profile.entityType || '',
        stateOfFormation: profile.stateOfFormation || '',
        authorizedSigner: profile.authorizedSigner || '',
        bankName: profile.bankName || '',
        bankAccountType: profile.bankAccountType as 'CHECKING' | 'SAVINGS' | undefined,
        // Note: sensitive fields are not pre-filled for security - only masked values are returned
        ssn: '',
        fein: '',
        routingNumber: '',
        accountNumber: '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zipCode: profile.zipCode || '',
      });
      // Initialize tax ID type toggle for TRUST/IRA profiles
      const pType = profile.type as InvestorProfileType;
      if (pType === 'TRUST' || pType === 'IRA') {
        setTaxIdType(profile.feinMasked ? 'fein' : 'ssn');
      }
      setError(null);
      setShowDeleteConfirm(false);
      setShowSaveConfirm(false);
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Show confirmation dialog instead of directly saving
    setShowSaveConfirm(true);
  };

  const confirmSave = async () => {
    if (!profile) return;

    setError(null);
    setIsSubmitting(true);

    try {
      // Strip empty sensitive fields so backend doesn't overwrite existing encrypted values
      const submitData = { ...formData };
      if (!submitData.ssn) delete submitData.ssn;
      if (!submitData.fein) delete submitData.fein;
      if (!submitData.routingNumber) delete submitData.routingNumber;
      if (!submitData.accountNumber) delete submitData.accountNumber;
      await adminService.updateInvestorProfile(profile.id, submitData);
      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
      setShowSaveConfirm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!profile) return;

    setError(null);
    setIsDeleting(true);

    try {
      await adminService.deleteInvestorProfile(profile.id);
      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!profile) return null;

  const isEntity = formData.type !== 'INDIVIDUAL';
  const isIndividual = formData.type === 'INDIVIDUAL';
  const isTrustOrIra = formData.type === 'TRUST' || formData.type === 'IRA';
  const isStandardEntity = formData.type === 'LLC' || formData.type === 'CORPORATION' || formData.type === 'PARTNERSHIP';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 my-8 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Investment Profile</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {showSaveConfirm ? (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">Confirm Save Changes</p>
              <p className="text-sm text-blue-600 mt-1">
                Are you sure you want to save these changes to the investment profile?
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setShowSaveConfirm(false)}
                disabled={isSubmitting}
              >
                Go Back
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={confirmSave}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Yes, Save Changes'}
              </Button>
            </div>
          </div>
        ) : showDeleteConfirm ? (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">Are you sure you want to delete this profile?</p>
              <p className="text-sm text-red-600 mt-1">
                This will also delete all investments associated with this profile. This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete Profile'}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Profile Type *</label>
              <div className="grid grid-cols-3 gap-2">
                {(['INDIVIDUAL', 'LLC', 'TRUST', 'CORPORATION', 'PARTNERSHIP', 'IRA'] as InvestorProfileType[]).map(
                  (type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({ ...formData, type })}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        formData.type === type
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {type}
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Personal/Entity Info */}
            <div className="grid grid-cols-2 gap-4">
              {!isEntity ? (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name *</label>
                  <input
                    type="text"
                    value={formData.legalName || ''}
                    onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Full legal name"
                  />
                </div>
              ) : (
                <>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entity Name *</label>
                    <input
                      type="text"
                      value={formData.entityName || ''}
                      onChange={(e) => setFormData({ ...formData, entityName: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Legal entity name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State of Formation</label>
                    <input
                      type="text"
                      value={formData.stateOfFormation || ''}
                      onChange={(e) => setFormData({ ...formData, stateOfFormation: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., Delaware"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Authorized Signer</label>
                    <input
                      type="text"
                      value={formData.authorizedSigner || ''}
                      onChange={(e) => setFormData({ ...formData, authorizedSigner: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Name of authorized signer"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Tax ID */}
            {canViewSensitive ? (
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Tax Identification</h3>

              {/* INDIVIDUAL: Always SSN */}
              {isIndividual && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SSN</label>
                  {profile.ssnMasked && (
                    <p className="text-xs text-gray-500 mb-1">Current: ***-**-{profile.ssnMasked}</p>
                  )}
                  <input
                    type="text"
                    value={formData.ssn || ''}
                    onChange={(e) => setFormData({ ...formData, ssn: e.target.value, fein: undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="XXX-XX-XXXX"
                  />
                </div>
              )}

              {/* TRUST/IRA: Toggle between SSN or FEIN */}
              {isTrustOrIra && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID Type</label>
                  {((taxIdType === 'ssn' && profile.ssnMasked) || (taxIdType === 'fein' && profile.feinMasked)) && (
                    <p className="text-xs text-gray-500 mb-2">
                      Current: {taxIdType === 'ssn' ? `***-**-${profile.ssnMasked}` : `**-***${profile.feinMasked}`}
                    </p>
                  )}
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        setTaxIdType('ssn');
                        setFormData({ ...formData, fein: undefined });
                      }}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        taxIdType === 'ssn'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      SSN
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTaxIdType('fein');
                        setFormData({ ...formData, ssn: undefined });
                      }}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        taxIdType === 'fein'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      FEIN
                    </button>
                  </div>
                  <input
                    type="text"
                    value={taxIdType === 'ssn' ? formData.ssn || '' : formData.fein || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        [taxIdType === 'ssn' ? 'ssn' : 'fein']: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder={taxIdType === 'ssn' ? 'XXX-XX-XXXX' : 'XX-XXXXXXX'}
                  />
                </div>
              )}

              {/* Standard Entities: Always FEIN */}
              {isStandardEntity && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">FEIN (Tax ID)</label>
                  {profile.feinMasked && (
                    <p className="text-xs text-gray-500 mb-1">Current: **-***{profile.feinMasked}</p>
                  )}
                  <input
                    type="text"
                    value={formData.fein || ''}
                    onChange={(e) => setFormData({ ...formData, fein: e.target.value, ssn: undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="XX-XXXXXXX"
                  />
                </div>
              )}

              <p className="mt-2 text-xs text-gray-500">
                Leave blank to keep existing value. This will be encrypted and stored securely.
              </p>
            </div>
            ) : (
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Tax Identification</h3>
              <p className="text-sm text-gray-500 italic">You do not have permission to edit sensitive data.</p>
            </div>
            )}

            {/* Banking Info */}
            {canViewSensitive ? (
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Banking Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={formData.bankName || ''}
                    onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Chase Bank"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                  <select
                    value={formData.bankAccountType || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, bankAccountType: e.target.value as 'CHECKING' | 'SAVINGS' })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select type</option>
                    <option value="CHECKING">Checking</option>
                    <option value="SAVINGS">Savings</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number</label>
                  <input
                    type="text"
                    value={formData.routingNumber || ''}
                    onChange={(e) => setFormData({ ...formData, routingNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder={profile.routingNumberMasked || '9 digits'}
                    maxLength={9}
                  />
                  {profile.routingNumberMasked && !formData.routingNumber && (
                    <p className="mt-1 text-xs text-gray-500">Current: {profile.routingNumberMasked} (leave blank to keep)</p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                  <input
                    type="text"
                    value={formData.accountNumber || ''}
                    onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder={profile.accountNumberMasked || 'Account number'}
                  />
                  {profile.accountNumberMasked && !formData.accountNumber && (
                    <p className="mt-1 text-xs text-gray-500">Current: {profile.accountNumberMasked} (leave blank to keep)</p>
                  )}
                </div>
              </div>
            </div>
            ) : (
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Banking Information</h3>
              <p className="text-sm text-gray-500 italic">You do not have permission to edit sensitive data.</p>
            </div>
            )}

            {/* Address */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Mailing Address</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    type="text"
                    value={formData.address || ''}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={formData.state || ''}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                    <input
                      type="text"
                      value={formData.zipCode || ''}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>

            {profile.investmentCount === 0 && canDeleteInvestor && (
              <div className="pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Delete This Profile
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Press-to-Reveal Component (shows data only while button is held)
// =============================================================================

interface PressToRevealProps {
  label: string;
  maskedValue: string;
  revealedValue: string | null;
  className?: string;
}

function PressToReveal({ label, maskedValue, revealedValue, className = '' }: PressToRevealProps) {
  const [isRevealed, setIsRevealed] = useState(false);

  const handleMouseDown = () => setIsRevealed(true);
  const handleMouseUp = () => setIsRevealed(false);
  const handleMouseLeave = () => setIsRevealed(false);
  const handleTouchStart = () => setIsRevealed(true);
  const handleTouchEnd = () => setIsRevealed(false);

  return (
    <div className={className}>
      <p className="text-xs text-gray-500">{label}</p>
      <div className="flex items-center gap-3">
        <p className="text-lg font-mono font-medium text-gray-900 flex-1">
          {isRevealed ? (revealedValue || 'Not provided') : maskedValue}
        </p>
        {revealedValue && (
          <button
            type="button"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors select-none ${
              isRevealed
                ? 'bg-amber-100 border-amber-300 text-amber-800'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-1">
              <EyeIcon className="h-3.5 w-3.5" />
              {isRevealed ? 'Viewing...' : 'Hold to View'}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// View Profile Modal (Admin - Shows all sensitive data)
// =============================================================================

interface ViewProfileModalProps {
  profile: InvestorDetails['profiles'][0] | null;
  onClose: () => void;
}

function ViewProfileModal({ profile, onClose }: ViewProfileModalProps) {
  const { hasPermission } = usePermissions();
  const canViewSensitive = hasPermission('investors', 'sensitive');
  const [loading, setLoading] = useState(canViewSensitive);
  const [error, setError] = useState<string | null>(null);
  const [sensitiveData, setSensitiveData] = useState<{
    ssn: string | null;
    fein: string | null;
    routingNumber: string | null;
    accountNumber: string | null;
  } | null>(null);

  useEffect(() => {
    if (profile && canViewSensitive) {
      loadSensitiveData();
    }
  }, [profile, canViewSensitive]);

  const loadSensitiveData = async () => {
    if (!profile) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getProfileSensitiveData(profile.id);
      setSensitiveData(data);
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setLoading(false);
    }
  };

  // Format SSN as XXX-XX-XXXX
  const formatSSN = (ssn: string | null): string => {
    if (!ssn) return 'Not provided';
    const digits = ssn.replace(/\D/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    }
    return ssn;
  };

  // Format FEIN as XX-XXXXXXX
  const formatFEIN = (fein: string | null): string => {
    if (!fein) return 'Not provided';
    const digits = fein.replace(/\D/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    return fein;
  };

  // Mask SSN showing only last 4 digits
  const maskSSN = (ssn: string | null): string => {
    if (!ssn) return '***-**-****';
    const digits = ssn.replace(/\D/g, '');
    if (digits.length >= 4) {
      return `***-**-${digits.slice(-4)}`;
    }
    return '***-**-****';
  };

  // Mask FEIN showing only last 4 digits
  const maskFEIN = (fein: string | null): string => {
    if (!fein) return '**-***' + '****';
    const digits = fein.replace(/\D/g, '');
    if (digits.length >= 4) {
      return `**-***${digits.slice(-4)}`;
    }
    return '**-***' + '****';
  };

  // Mask routing number showing only last 4 digits
  const maskRouting = (routing: string | null): string => {
    if (!routing) return '*****' + '****';
    if (routing.length >= 4) {
      return '*****' + routing.slice(-4);
    }
    return '*****' + '****';
  };

  // Mask account number showing only last 4 digits
  const maskAccount = (account: string | null): string => {
    if (!account) return '****' + '****';
    if (account.length >= 4) {
      return '****' + account.slice(-4);
    }
    return '****' + '****';
  };

  if (!profile) return null;

  const isEntity = profile.type !== 'INDIVIDUAL';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 my-8 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">View Investment Profile</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="py-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent" />
            <p className="mt-2 text-sm text-gray-500">Loading profile data...</p>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Profile Info */}
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary-100 rounded-lg">
                {isEntity ? (
                  <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
                ) : (
                  <UserCircleIcon className="h-6 w-6 text-primary-600" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {profile.entityName || profile.legalName || 'Unnamed Profile'}
                </h3>
                <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded mt-1">
                  {profile.type}
                </span>
              </div>
            </div>

            {/* Entity Info (if applicable) */}
            {isEntity && (
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Entity Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  {profile.stateOfFormation && (
                    <div>
                      <p className="text-xs text-gray-500">State of Formation</p>
                      <p className="text-sm font-medium text-gray-900">{profile.stateOfFormation}</p>
                    </div>
                  )}
                  {profile.authorizedSigner && (
                    <div>
                      <p className="text-xs text-gray-500">Authorized Signer</p>
                      <p className="text-sm font-medium text-gray-900">{profile.authorizedSigner}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tax ID - determine type from actual data, not just profile type (TRUST/IRA can have either SSN or FEIN) */}
            {canViewSensitive ? (
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Tax Information</h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <EyeIcon className="h-4 w-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-800">Sensitive Data - Hold button to view</span>
                  </div>
                  {(() => {
                    // Determine which tax ID to show based on actual data present
                    // For INDIVIDUAL: always SSN. For LLC/CORP/PARTNERSHIP: always FEIN.
                    // For TRUST/IRA: check which one actually exists.
                    const hasSSN = !!(profile.ssnMasked || sensitiveData?.ssn);
                    const hasFEIN = !!(profile.feinMasked || sensitiveData?.fein);
                    const showFEIN = isEntity && !hasSSN ? true : hasFEIN && !hasSSN;

                    return showFEIN ? (
                      <PressToReveal
                        label="FEIN (Tax ID)"
                        maskedValue={maskFEIN(sensitiveData?.fein || null)}
                        revealedValue={formatFEIN(sensitiveData?.fein || null)}
                      />
                    ) : (
                      <PressToReveal
                        label="Social Security Number"
                        maskedValue={maskSSN(sensitiveData?.ssn || null)}
                        revealedValue={formatSSN(sensitiveData?.ssn || null)}
                      />
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Tax Information</h3>
                <p className="text-sm text-gray-500 italic">You do not have permission to view sensitive data.</p>
              </div>
            )}

            {/* Banking Information */}
            {canViewSensitive ? (
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Banking Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Bank Name</p>
                    <p className="text-sm font-medium text-gray-900">{profile.bankName || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Account Type</p>
                    <p className="text-sm font-medium text-gray-900">{profile.bankAccountType || 'Not provided'}</p>
                  </div>
                </div>
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <EyeIcon className="h-4 w-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-800">Sensitive Data - Hold button to view</span>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <PressToReveal
                      label="Routing Number"
                      maskedValue={maskRouting(sensitiveData?.routingNumber || null)}
                      revealedValue={sensitiveData?.routingNumber || null}
                    />
                    <PressToReveal
                      label="Account Number"
                      maskedValue={maskAccount(sensitiveData?.accountNumber || null)}
                      revealedValue={sensitiveData?.accountNumber || null}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Banking Information</h3>
                <p className="text-sm text-gray-500 italic">You do not have permission to view sensitive data.</p>
              </div>
            )}

            {/* Mailing Address */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Mailing Address</h3>
              <p className="text-sm text-gray-900">
                {profile.address ? (
                  <>
                    {profile.address}
                    <br />
                    {profile.city}, {profile.state} {profile.zipCode}
                  </>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>

            {/* Investment Summary */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Investment Summary</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Investments</p>
                  <p className="text-sm font-medium text-gray-900">{profile.investmentCount}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Invested</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                    }).format(profile.totalInvested)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Add Investment Modal
// =============================================================================

interface AddInvestmentModalProps {
  isOpen: boolean;
  investor: InvestorDetails;
  onClose: () => void;
  onCreated: () => void;
}

function AddInvestmentModal({ isOpen, investor, onClose, onCreated }: AddInvestmentModalProps) {
  const [deals, setDeals] = useState<DealWithStats[]>([]);
  const [formData, setFormData] = useState({
    profileId: '',
    dealId: '',
    investmentAmount: 0,
    investmentDate: new Date().toISOString().split('T')[0],
    shareClassId: '',
  });
  const [shareClasses, setShareClasses] = useState<ShareClass[]>([]);
  const [loadingShareClasses, setLoadingShareClasses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadDeals();
      // Reset form when modal opens
      setFormData({
        profileId: investor.profiles.length > 0 ? investor.profiles[0].id : '',
        dealId: '',
        investmentAmount: 0,
        investmentDate: new Date().toISOString().split('T')[0],
        shareClassId: '',
      });
      setShareClasses([]);
      setError(null);
    }
  }, [isOpen, investor.profiles]);

  const loadDeals = async () => {
    try {
      const data = await adminService.listDeals();
      setDeals(data);
    } catch (err) {
      console.error('Failed to load deals:', err);
    }
  };

  // Load share classes when deal changes
  useEffect(() => {
    if (!formData.dealId) {
      setShareClasses([]);
      setFormData(prev => ({ ...prev, shareClassId: '' }));
      return;
    }
    const loadShareClasses = async () => {
      try {
        setLoadingShareClasses(true);
        const classes = await adminService.getShareClasses(formData.dealId);
        setShareClasses(classes);
        // Auto-select default class
        const defaultClass = classes.find((sc: ShareClass) => sc.isDefault) || classes[0];
        if (defaultClass) {
          setFormData(prev => ({ ...prev, shareClassId: defaultClass.id }));
        }
      } catch (err) {
        console.error('Failed to load share classes:', err);
      } finally {
        setLoadingShareClasses(false);
      }
    };
    loadShareClasses();
  }, [formData.dealId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (shareClasses.length > 0 && !formData.shareClassId) {
      setError('Please select a share class');
      return;
    }

    setIsSubmitting(true);

    try {
      await adminService.createInvestment({
        profileId: formData.profileId,
        dealId: formData.dealId,
        investmentAmount: formData.investmentAmount,
        investmentDate: formData.investmentDate,
        shareClassId: formData.shareClassId || undefined,
      });
      await onCreated();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add Investment</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investment Profile *</label>
            <select
              value={formData.profileId}
              onChange={(e) => setFormData({ ...formData, profileId: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select a profile</option>
              {investor.profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.entityName || profile.legalName} ({profile.type})
                </option>
              ))}
            </select>
            {investor.profiles.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">
                No profiles available. Create an investment profile first.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal *</label>
            <select
              value={formData.dealId}
              onChange={(e) => setFormData({ ...formData, dealId: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select a deal</option>
              {deals.map((deal) => (
                <option key={deal.id} value={deal.id}>
                  {deal.name} - {deal.city}, {deal.state}
                </option>
              ))}
            </select>
          </div>

          {/* Share Class Selection - shown when deal has share classes */}
          {formData.dealId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Share Class{shareClasses.length > 0 ? ' *' : ''}
              </label>
              {loadingShareClasses ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                  Loading share classes...
                </div>
              ) : shareClasses.length > 0 ? (
                <select
                  value={formData.shareClassId}
                  onChange={(e) => setFormData({ ...formData, shareClassId: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select a share class</option>
                  {shareClasses.map((sc) => (
                    <option key={sc.id} value={sc.id}>
                      {sc.name} ({sc.code})
                      {sc.isDefault ? ' - Default' : ''}
                      {sc.preferredReturnRate ? ` - ${(sc.preferredReturnRate * 100).toFixed(1)}% Pref` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm">
                  No share classes configured for this deal
                </div>
              )}
              {shareClasses.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Required: Assign investment to a share class for waterfall distributions
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investment Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={formData.investmentAmount ? formData.investmentAmount.toLocaleString('en-US') : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFormData({ ...formData, investmentAmount: parseFloat(value) || 0 });
                }}
                required
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="100,000"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Ownership % will be calculated automatically based on total equity raised.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investment Date</label>
            <input
              type="date"
              value={formData.investmentDate}
              onChange={(e) => setFormData({ ...formData, investmentDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting || investor.profiles.length === 0}
            >
              {isSubmitting ? 'Creating...' : 'Add Investment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Edit Investment Modal
// =============================================================================

interface EditInvestmentModalProps {
  investment: InvestorDetails['investments'][0] | null;
  onClose: () => void;
  onSaved: () => void;
}

function EditInvestmentModal({ investment, onClose, onSaved }: EditInvestmentModalProps) {
  const { hasPermission } = usePermissions();
  const canDeleteDeal = hasPermission('deals', 'delete');
  const [contributions, setContributions] = useState<InvestmentContribution[]>([]);
  const [currentOwnership, setCurrentOwnership] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Share class state
  const [shareClasses, setShareClasses] = useState<ShareClass[]>([]);
  const [selectedShareClassId, setSelectedShareClassId] = useState<string>(investment?.shareClassId || '');
  const [shareClassChanged, setShareClassChanged] = useState(false);

  // New contribution form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContribution, setNewContribution] = useState({ amount: 0, fundingDate: '', notes: '' });

  // Edit contribution
  const [editingContributionId, setEditingContributionId] = useState<string | null>(null);
  const [editContribution, setEditContribution] = useState({ amount: 0, fundingDate: '', notes: '' });

  // Track the investment ID to only reset state when opening a DIFFERENT investment
  const [lastInvestmentId, setLastInvestmentId] = useState<string | null>(null);

  // Delete confirmation
  const [showInvestmentDeleteConfirm, setShowInvestmentDeleteConfirm] = useState(false);
  const [investmentDeleteText, setInvestmentDeleteText] = useState('');
  const [showContributionDeleteConfirm, setShowContributionDeleteConfirm] = useState(false);
  const [contributionDeleteText, setContributionDeleteText] = useState('');
  const [deletingContributionId, setDeletingContributionId] = useState<string | null>(null);

  useEffect(() => {
    // Only reset state when opening a different investment (not when parent refreshes same investment)
    if (investment && investment.id !== lastInvestmentId) {
      setContributions(investment.contributions || []);
      setCurrentOwnership(investment.ownershipPercentage);
      setTotalAmount(investment.investmentAmount);
      setSelectedShareClassId(investment.shareClassId || '');
      setShareClassChanged(false);
      setError(null);
      setShowAddForm(false);
      setEditingContributionId(null);
      setNewContribution({ amount: 0, fundingDate: '', notes: '' });
      setLastInvestmentId(investment.id);
    }
  }, [investment, lastInvestmentId]);

  // Load share classes for the investment's deal
  useEffect(() => {
    if (!investment?.dealId) return;
    const loadShareClasses = async () => {
      try {
        const classes = await adminService.getShareClasses(investment.dealId);
        setShareClasses(classes);
      } catch (err) {
        console.error('Failed to load share classes:', err);
      }
    };
    loadShareClasses();
  }, [investment?.dealId]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const handleAddContribution = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!investment) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await adminService.addContribution(investment.id, {
        amount: newContribution.amount,
        fundingDate: newContribution.fundingDate,
        notes: newContribution.notes || undefined,
      });

      // Add new contribution to local state
      setContributions(prev => [...prev, {
        id: result.id,
        amount: result.amount,
        fundingDate: result.fundingDate,
        notes: result.notes || '',
        createdAt: new Date().toISOString(),
      }]);
      setTotalAmount(result.investment.investmentAmount);
      setCurrentOwnership(result.investment.ownershipPercentage);

      // Refresh parent data and reset form
      await onSaved();
      setShowAddForm(false);
      setNewContribution({ amount: 0, fundingDate: '', notes: '' });
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateContribution = async (contributionId: string) => {
    if (!investment) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await adminService.updateContribution(contributionId, {
        amount: editContribution.amount,
        fundingDate: editContribution.fundingDate,
        notes: editContribution.notes || undefined,
      });

      // Update local state immediately with API result values
      setContributions(prev => prev.map(c =>
        c.id === contributionId
          ? { ...c, amount: result.amount, fundingDate: result.fundingDate, notes: result.notes || '' }
          : c
      ));
      setTotalAmount(result.investment.investmentAmount);
      setCurrentOwnership(result.investment.ownershipPercentage);

      // Close edit form and refresh parent data
      setEditingContributionId(null);
      await onSaved();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteContributionClick = (contributionId: string) => {
    if (!investment) return;
    if (contributions.length <= 1) {
      setError('Cannot delete the last contribution. Delete the investment instead.');
      return;
    }
    setDeletingContributionId(contributionId);
    setContributionDeleteText('');
    setShowContributionDeleteConfirm(true);
  };

  const handleDeleteContributionConfirm = async () => {
    if (!deletingContributionId || contributionDeleteText.toLowerCase() !== 'proceed') return;

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await adminService.deleteContribution(deletingContributionId);

      // Remove contribution from local state
      setContributions(prev => prev.filter(c => c.id !== deletingContributionId));
      setTotalAmount(result.investment.investmentAmount);
      setCurrentOwnership(result.investment.ownershipPercentage);
      setShowContributionDeleteConfirm(false);
      setDeletingContributionId(null);

      // Refresh parent data
      await onSaved();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteInvestmentClick = () => {
    setInvestmentDeleteText('');
    setShowInvestmentDeleteConfirm(true);
  };

  const handleDeleteInvestmentConfirm = async () => {
    if (!investment || investmentDeleteText.toLowerCase() !== 'proceed') return;

    setIsSubmitting(true);
    try {
      await adminService.deleteInvestment(investment.id);
      setShowInvestmentDeleteConfirm(false);
      await onSaved();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditContribution = (contrib: InvestmentContribution) => {
    setEditingContributionId(contrib.id);
    setEditContribution({
      amount: contrib.amount,
      fundingDate: new Date(contrib.fundingDate).toISOString().split('T')[0],
      notes: contrib.notes || '',
    });
  };

  if (!investment) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 my-8 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Investment Details</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Investment Summary */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm font-medium text-gray-900">{investment.dealName}</p>
              <p className="text-xs text-gray-500">via {investment.profileName}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900">{formatCurrency(totalAmount)}</p>
              <p className="text-sm text-gray-500">{currentOwnership.toFixed(2)}% ownership</p>
            </div>
          </div>
        </div>

        {/* Share Class */}
        {shareClasses.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Share Class *</label>
            <select
              value={selectedShareClassId}
              onChange={(e) => {
                setSelectedShareClassId(e.target.value);
                setShareClassChanged(true);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select a share class</option>
              {shareClasses.map((sc) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name} ({sc.code})
                  {sc.isDefault ? ' - Default' : ''}
                </option>
              ))}
            </select>
            {shareClassChanged && (
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  disabled={isSubmitting || (shareClasses.length > 0 && !selectedShareClassId)}
                  onClick={async () => {
                    if (shareClasses.length > 0 && !selectedShareClassId) {
                      setError('Please select a share class');
                      return;
                    }
                    setIsSubmitting(true);
                    setError(null);
                    try {
                      await adminService.updateInvestment(investment.id, {
                        shareClassId: selectedShareClassId || null,
                      });
                      setShareClassChanged(false);
                      await onSaved();
                    } catch (err) {
                      setError(getApiError(err).message);
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                >
                  {isSubmitting ? 'Saving...' : 'Save Share Class'}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setSelectedShareClassId(investment.shareClassId || '');
                    setShareClassChanged(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Contributions Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Funding Contributions</h3>
            {!showAddForm && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-1"
              >
                <PlusIcon className="h-4 w-4" />
                Add Contribution
              </Button>
            )}
          </div>

          {/* Add Contribution Form */}
          {showAddForm && (
            <form onSubmit={handleAddContribution} className="mb-4 p-4 border border-primary-200 bg-primary-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-3">New Contribution</h4>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Amount *</label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={newContribution.amount ? newContribution.amount.toLocaleString('en-US') : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setNewContribution({ ...newContribution, amount: parseFloat(value) || 0 });
                      }}
                      required
                      className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Funding Date *</label>
                  <input
                    type="date"
                    value={newContribution.fundingDate}
                    onChange={(e) => setNewContribution({ ...newContribution, fundingDate: e.target.value })}
                    required
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <input
                  type="text"
                  value={newContribution.notes}
                  onChange={(e) => setNewContribution({ ...newContribution, notes: e.target.value })}
                  placeholder="e.g., Additional capital call"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={isSubmitting}>
                  {isSubmitting ? 'Adding...' : 'Add Contribution'}
                </Button>
              </div>
            </form>
          )}

          {/* Contributions List */}
          <div className="space-y-2">
            {contributions.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No contributions recorded</p>
            ) : (
              contributions.map((contrib) => (
                <div key={contrib.id} className="border border-gray-200 rounded-lg p-3">
                  {editingContributionId === contrib.id ? (
                    // Edit form
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={editContribution.amount ? editContribution.amount.toLocaleString('en-US') : ''}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9.]/g, '');
                                setEditContribution(prev => ({ ...prev, amount: parseFloat(value) || 0 }));
                              }}
                              className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                          <input
                            type="date"
                            value={editContribution.fundingDate}
                            onChange={(e) => setEditContribution(prev => ({ ...prev, fundingDate: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <input
                          type="text"
                          value={editContribution.notes}
                          onChange={(e) => setEditContribution(prev => ({ ...prev, notes: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => setEditingContributionId(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleUpdateContribution(contrib.id)}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Display view
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-900">{formatCurrency(contrib.amount)}</span>
                          <span className="text-sm text-gray-500">{formatDate(contrib.fundingDate)}</span>
                        </div>
                        {contrib.notes && (
                          <p className="text-xs text-gray-500 mt-0.5">{contrib.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => startEditContribution(contrib)}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        {contributions.length > 1 && canDeleteDeal && (
                          <button
                            type="button"
                            onClick={() => handleDeleteContributionClick(contrib.id)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleDeleteInvestmentClick}
            disabled={isSubmitting}
            className="text-red-600 hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete Investment
          </Button>
          <div className="flex-1" />
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Close
          </Button>
        </div>

        {/* Delete Investment Confirmation Modal */}
        {showInvestmentDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Investment</h3>
              </div>

              <p className="text-sm text-gray-600 mb-2">
                You are about to permanently delete this investment:
              </p>
              <p className="text-sm font-medium text-gray-900 mb-1 p-2 bg-gray-50 rounded-lg">
                {investment?.dealName} - {investment?.profileName}
              </p>
              <p className="text-sm text-red-600 mb-4">
                All contributions will be deleted. This action cannot be undone.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-bold">proceed</span> below to confirm deletion:
                </label>
                <input
                  type="text"
                  value={investmentDeleteText}
                  onChange={(e) => setInvestmentDeleteText(e.target.value)}
                  placeholder="Type 'proceed' to confirm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  autoFocus
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowInvestmentDeleteConfirm(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteInvestmentConfirm}
                  disabled={investmentDeleteText.toLowerCase() !== 'proceed' || isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Investment'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Contribution Confirmation Modal */}
        {showContributionDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Contribution</h3>
              </div>

              <p className="text-sm text-gray-600 mb-2">
                You are about to permanently delete this contribution.
              </p>
              <p className="text-sm text-red-600 mb-4">
                The investment total will be recalculated. This action cannot be undone.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-bold">proceed</span> below to confirm deletion:
                </label>
                <input
                  type="text"
                  value={contributionDeleteText}
                  onChange={(e) => setContributionDeleteText(e.target.value)}
                  placeholder="Type 'proceed' to confirm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  autoFocus
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowContributionDeleteConfirm(false);
                    setDeletingContributionId(null);
                    setContributionDeleteText('');
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteContributionConfirm}
                  disabled={contributionDeleteText.toLowerCase() !== 'proceed' || isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Contribution'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function InvestorDetailPage() {
  const { investorId } = useParams<{ investorId: string }>();
  const navigate = useNavigate();
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canEditInvestor = hasPermission('investors', 'edit');
  const canEditDeal = hasPermission('deals', 'edit');
  const [isLoading, setIsLoading] = useState(true);
  const [investor, setInvestor] = useState<InvestorDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddProfileModal, setShowAddProfileModal] = useState(false);
  const [showAddInvestmentModal, setShowAddInvestmentModal] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<InvestorDetails['investments'][0] | null>(null);
  const [editingProfile, setEditingProfile] = useState<InvestorDetails['profiles'][0] | null>(null);
  const [viewingProfile, setViewingProfile] = useState<InvestorDetails['profiles'][0] | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'profiles' | 'investments' | 'documents' | 'distributions'>('overview');
  const [distributions, setDistributions] = useState<InvestorDistribution[]>([]);
  const [distributionsLoading, setDistributionsLoading] = useState(false);
  const [documents, setDocuments] = useState<InvestorDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<{ id: string; name: string; mimeType?: string } | null>(null);

  useEffect(() => {
    if (investorId) {
      loadInvestor();
    }
  }, [investorId]);

  useEffect(() => {
    if (activeTab === 'distributions' && investorId && distributions.length === 0) {
      loadDistributions();
    }
    if (activeTab === 'documents' && investorId && documents.length === 0) {
      loadDocuments();
    }
  }, [activeTab, investorId]);

  const loadDistributions = async () => {
    if (!investorId) return;
    setDistributionsLoading(true);
    try {
      const data = await adminService.getInvestorDistributions(investorId);
      setDistributions(data);
    } catch (err) {
      console.error('Failed to load distributions:', err);
    } finally {
      setDistributionsLoading(false);
    }
  };

  const loadDocuments = async () => {
    if (!investorId) return;
    setDocumentsLoading(true);
    try {
      const data = await adminService.getInvestorDocuments(investorId);
      setDocuments(data);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const loadInvestor = async () => {
    if (!investorId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminService.getInvestor(investorId);
      setInvestor(data);
    } catch (err) {
      console.error('Failed to load investor:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('investors', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-32 bg-gray-200 rounded-xl" />
        <div className="h-64 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (error || !investor) {
    return (
      <div className="space-y-6">
        <Link to="/admin/investors" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Investors
        </Link>
        <Card padding="lg">
          <div className="text-center py-12">
            <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {error || 'Investor not found'}
            </h3>
            <Button className="mt-4" onClick={() => navigate('/admin/investors')}>
              Return to Investors
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: UserCircleIcon },
    { id: 'profiles', label: 'Profiles', icon: BuildingOfficeIcon },
    { id: 'investments', label: 'Investments', icon: BanknotesIcon },
    { id: 'documents', label: 'Documents', icon: DocumentTextIcon },
    { id: 'distributions', label: 'Distribution Schedule', icon: CalendarDaysIcon },
  ] as const;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/investors"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-xl font-semibold text-primary-700">
                {investor.name.charAt(0)}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{investor.name}</h1>
              <p className="text-sm text-gray-500">{investor.email}</p>
            </div>
          </div>
        </div>
        {canEditInvestor && (
          <Button onClick={() => setShowEditModal(true)} className="flex items-center gap-2">
            <PencilIcon className="h-4 w-4" />
            Edit
          </Button>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <p className="text-sm text-gray-500">Profiles</p>
          <p className="text-2xl font-bold text-gray-900">{investor.profiles.length}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Investments</p>
          <p className="text-2xl font-bold text-gray-900">{investor.investments?.length || 0}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Invested</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(investor.investments?.reduce((sum, inv) => sum + inv.investmentAmount, 0) || 0)}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Distributed</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(investor.investments?.reduce((sum, inv) => sum + inv.totalDistributed, 0) || 0)}
          </p>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact Info */}
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-900">{investor.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <PhoneIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-900">{formatPhoneDisplay(investor.phone) || 'No phone number'}</span>
              </div>
            </div>
          </Card>

          {/* Account Status */}
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Status</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Authenticator (TOTP)</span>
                <span className={`flex items-center gap-1 text-sm ${investor.totpEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {investor.totpEnabled ? <CheckIcon className="h-4 w-4" /> : <XMarkIcon className="h-4 w-4" />}
                  {investor.totpEnabled ? 'Enabled' : 'Not set up'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Last Login</span>
                <span className="text-sm text-gray-900">{formatDate(investor.lastLoginAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Account Created</span>
                <span className="text-sm text-gray-900">{formatDate(investor.createdAt)}</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'profiles' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            {canEditInvestor && (
              <Button onClick={() => setShowAddProfileModal(true)} className="flex items-center gap-2">
                <PlusIcon className="h-4 w-4" />
                Add Profile
              </Button>
            )}
          </div>

          {investor.profiles.length === 0 ? (
            <Card padding="lg">
              <div className="text-center py-8">
                <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Profiles Yet</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Create an investment profile for this investor
                </p>
                {canEditInvestor && (
                  <Button className="mt-4" onClick={() => setShowAddProfileModal(true)}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Profile
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            investor.profiles.map((profile) => (
              <Card key={profile.id} padding="md">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary-100 rounded-lg">
                      {profile.type === 'INDIVIDUAL' ? (
                        <UserCircleIcon className="h-6 w-6 text-primary-600" />
                      ) : (
                        <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">
                          {profile.entityName || profile.legalName || 'Unnamed Profile'}
                        </h3>
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                          {profile.type}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Investments:</span>{' '}
                          <span className="font-medium">{profile.investmentCount}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Total Invested:</span>{' '}
                          <span className="font-medium">{formatCurrency(profile.totalInvested)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewingProfile(profile);
                      }}
                      title="View profile details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                    {canEditInvestor && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingProfile(profile);
                        }}
                        title="Edit profile"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {activeTab === 'investments' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            {canEditDeal && (
              <Button onClick={() => setShowAddInvestmentModal(true)} className="flex items-center gap-2">
                <PlusIcon className="h-4 w-4" />
                Add Investment
              </Button>
            )}
          </div>
          {!investor.investments || investor.investments.length === 0 ? (
            <Card padding="lg">
              <div className="text-center py-8">
                <BanknotesIcon className="h-12 w-12 text-gray-400 mx-auto" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Investments</h3>
                <p className="mt-2 text-sm text-gray-500">
                  This investor has not made any investments yet
                </p>
              </div>
            </Card>
          ) : (
            investor.investments.map((investment) => (
              <Card key={investment.id} padding="md" className={!investment.isActive ? 'opacity-60 border-l-4 border-l-red-400' : ''}>
                <div className="flex items-center justify-between">
                  <Link to={`/admin/deals/${investment.dealId}`} className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold ${investment.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                            {investment.dealName}
                          </h3>
                          {!investment.isActive && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                              Deactivated
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">via {investment.profileName}</p>
                      </div>
                      <div className="text-right mr-4">
                        <p className={`font-semibold ${investment.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                          {formatCurrency(investment.investmentAmount)}
                        </p>
                        <p className="text-sm text-gray-500">{investment.ownershipPercentage.toFixed(2)}% ownership</p>
                      </div>
                    </div>
                    {/* Contributions Summary */}
                    {investment.contributions && investment.contributions.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-500 mb-2">
                          Funding History ({investment.contributions.length} contribution{investment.contributions.length !== 1 ? 's' : ''})
                        </p>
                        <div className="space-y-1">
                          {investment.contributions.slice(0, 3).map((contrib, index) => (
                            <div key={contrib.id} className="flex items-center justify-between text-xs">
                              <span className="text-gray-500">
                                {new Date(contrib.fundingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                                {' - '}
                                {index === 0 ? 'Initial Funding' : 'Additional Contribution'}
                                {contrib.notes && ` - ${contrib.notes}`}
                              </span>
                              <span className="font-medium text-gray-700">{formatCurrency(contrib.amount)}</span>
                            </div>
                          ))}
                          {investment.contributions.length > 3 && (
                            <p className="text-xs text-gray-400">+{investment.contributions.length - 3} more</p>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="text-gray-500">Total Distributed:</span>
                      <span className="font-medium text-green-600">{formatCurrency(investment.totalDistributed)}</span>
                    </div>
                  </Link>
                  {canEditDeal && (
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingInvestment(investment)}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-4">
          {documentsLoading ? (
            <Card padding="lg">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-gray-200 rounded w-1/4" />
                <div className="h-10 bg-gray-200 rounded" />
                <div className="h-10 bg-gray-200 rounded" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
            </Card>
          ) : documents.length === 0 ? (
            <Card padding="lg">
              <div className="text-center py-8">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Documents</h3>
                <p className="mt-2 text-sm text-gray-500">
                  No documents have been shared with this investor yet
                </p>
              </div>
            </Card>
          ) : (
            <Card padding="none">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Document
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Deal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Access
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {documents.map((doc) => (
                      <tr
                        key={doc.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setViewingDocument({ id: doc.id, name: doc.name })}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                              {doc.description && (
                                <p className="text-xs text-gray-500 truncate max-w-xs">{doc.description}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                            {doc.type.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {doc.deal ? (
                            <Link
                              to={`/admin/deals/${doc.deal.id}`}
                              className="text-sm text-primary-600 hover:text-primary-700"
                            >
                              {doc.deal.name}
                            </Link>
                          ) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {doc.isSharedToAll ? (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              All Investors
                            </span>
                          ) : doc.investorProfile ? (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Personal
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                              Specific
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(doc.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'distributions' && (
        <div className="space-y-4">
          {distributionsLoading ? (
            <Card padding="lg">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-gray-200 rounded w-1/4" />
                <div className="h-10 bg-gray-200 rounded" />
                <div className="h-10 bg-gray-200 rounded" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
            </Card>
          ) : distributions.length === 0 ? (
            <Card padding="lg">
              <div className="text-center py-8">
                <CalendarDaysIcon className="h-12 w-12 text-gray-400 mx-auto" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Distributions</h3>
                <p className="mt-2 text-sm text-gray-500">
                  This investor has not received any distributions yet
                </p>
              </div>
            </Card>
          ) : (
            <Card padding="none">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Deal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Profile
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Distribution Total
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ownership %
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Investor Amount
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {distributions.map((dist) => (
                      <tr key={dist.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(dist.distributionDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/admin/deals/${dist.dealId}`}
                            className="text-sm font-medium text-primary-600 hover:text-primary-700"
                          >
                            {dist.dealName}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{dist.profileName}</div>
                          <div className="text-xs text-gray-500">{dist.profileType}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {dist.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                          {formatCurrency(dist.totalAmount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                          {dist.ownershipPercentage.toFixed(2)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-green-600">
                          {formatCurrency(dist.investorAmount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              dist.status === 'PUBLISHED'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {dist.status === 'PUBLISHED' ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={4} className="px-6 py-3 text-sm font-medium text-gray-900">
                        Total Distributed to Investor
                      </td>
                      <td colSpan={2} />
                      <td className="px-6 py-3 text-right text-sm font-bold text-green-600">
                        {formatCurrency(
                          distributions
                            .filter((d) => d.status === 'PUBLISHED')
                            .reduce((sum, d) => sum + d.investorAmount, 0)
                        )}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Modals */}
      <EditInvestorModal
        isOpen={showEditModal}
        investor={investor}
        onClose={() => setShowEditModal(false)}
        onSaved={loadInvestor}
      />

      <AddProfileModal
        isOpen={showAddProfileModal}
        investorId={investorId!}
        onClose={() => setShowAddProfileModal(false)}
        onCreated={loadInvestor}
      />

      <AddInvestmentModal
        isOpen={showAddInvestmentModal}
        investor={investor}
        onClose={() => setShowAddInvestmentModal(false)}
        onCreated={loadInvestor}
      />

      <EditInvestmentModal
        investment={editingInvestment}
        onClose={() => setEditingInvestment(null)}
        onSaved={loadInvestor}
      />

      <EditProfileModal
        profile={editingProfile}
        onClose={() => setEditingProfile(null)}
        onSaved={loadInvestor}
      />

      <ViewProfileModal
        profile={viewingProfile}
        onClose={() => setViewingProfile(null)}
      />

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewerModal
          isOpen={!!viewingDocument}
          onClose={() => setViewingDocument(null)}
          documentId={viewingDocument.id}
          fileName={viewingDocument.name}
          mimeType={viewingDocument.mimeType}
          getEmbedUrls={adminService.getDocumentViewUrl}
        />
      )}
    </div>
  );
}

export default InvestorDetailPage;
